import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import Funcionarios from './components/Funcionarios';
import ImportarJornada from './components/ImportarJornada';
import ImportarRegistros from './components/ImportarRegistros';
import RegistroPonto from './components/RegistroPonto';
import Relatorios from './components/Relatorios';
import Jornadas from './components/Jornadas';
import Afastamentos from './components/Afastamentos';
import Feriados from './components/Feriados';
import SaldoNegativo from './components/SaldoNegativo';
import PontoIndividual from './components/PontoIndividual';
import Compensacoes from './components/Compensacoes';
import Proposta from './components/Proposta';
import VrApuracao from './components/VrApuracao';
import JornadaExtra from './components/JornadaExtra';
import Login from './components/Login';
import RelatorioVR from './components/RelatorioVR';
import VRDetalhe          from './components/VRDetalhe';
import OcorrenciasJornada from './components/OcorrenciasJornada';

const TITLES = {
  '/dashboard':          'Dashboard',
  '/funcionarios':       'Funcionários',
  '/jornadas':           'Jornadas de Trabalho',
  '/importar':           'Importar Jornada',
  '/ponto-individual':   'Ponto Individual',
  '/importar-registros': 'Importar Folha de Ponto',
  '/registro-ponto':     'Registro de Ponto',
  '/relatorios':         'Relatórios',
  '/afastamentos':       'Afastamentos',
  '/relatorio-vr': 'Relatório de VR',
  '/vr-apuracao':        'Apuração de VR',
  '/jornada-extra':      'Jornada Extra',
  '/feriados':           'Feriados',
  '/saldo-negativo':     'Saldo Negativo',
  '/compensacoes':           'Compensações',
  '/proposta':               'Proposta de Compensação',
  '/ocorrencias-jornada':    'Ocorrências de Jornada',
};

function Layout({ children, user, onLogout }) {
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const location = useLocation();
  const title = TITLES[location.pathname] || 'App Ponto';

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const dateStr = now.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' });
  const timeStr = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div className="d-flex">
      <Sidebar open={open} onClose={() => setOpen(false)} />
      <div className="main-content flex-grow-1">
        <div className="topbar">
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-sm border-0 p-1 d-md-none" onClick={() => setOpen(!open)}>
              <i className="bi bi-list fs-5"></i>
            </button>
            <div className="topbar-title">
              <i className="bi bi-clock-history"></i>
              {title}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div className="topbar-date">
              <i className="bi bi-clock topbar-date-icon" />
              <span className="topbar-date-text">{dateStr}</span>
              <span className="topbar-date-sep">·</span>
              <span className="topbar-date-time">{timeStr}</span>
            </div>
            {user && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#f1f5f9', borderRadius: 8, padding: '4px 10px 4px 7px' }}>
                  <div style={{ width: 26, height: 26, background: 'linear-gradient(135deg,#1e3a8a,#2563eb)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="bi bi-person-fill" style={{ color: '#fff', fontSize: '0.75rem' }} />
                  </div>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>{user.username}</span>
                </div>
                <button
                  onClick={onLogout}
                  title="Sair"
                  style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 7, padding: '4px 8px', cursor: 'pointer', color: '#64748b', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <i className="bi bi-box-arrow-right" />
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="page-content">{children}</div>
      </div>
    </div>
  );
}

function RequireAuth({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  const [user, setUser] = useState(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    try {
      // Decodifica payload do JWT para pegar o username
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp * 1000 < Date.now()) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        return null;
      }
      return { username: payload.username || payload.user_id };
    } catch {
      return null;
    }
  });

  const handleLogin = (userData) => setUser(userData);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
  };

  return (
    <Router>
      <Routes>
        {/* Login — público */}
        <Route path="/login" element={
          user ? <Navigate to="/dashboard" replace /> : <Login onLogin={handleLogin} />
        } />

        {/* Raiz */}
        <Route path="/" element={<Navigate to={user ? '/dashboard' : '/login'} replace />} />

        {/* Rotas protegidas */}
        <Route path="/*" element={
          <RequireAuth user={user}>
            <Layout user={user} onLogout={handleLogout}>
              <Routes>
                <Route path="/dashboard"           element={<Dashboard />} />
                <Route path="/funcionarios"        element={<Funcionarios />} />
                <Route path="/jornadas"            element={<Jornadas />} />
                <Route path="/importar"            element={<ImportarJornada />} />
                <Route path="/importar-registros"  element={<ImportarRegistros />} />
                <Route path="/registro-ponto"      element={<RegistroPonto />} />
                <Route path="/ponto-individual"    element={<PontoIndividual />} />
                <Route path="/saldo-negativo"      element={<SaldoNegativo />} />
                <Route path="/proposta"            element={<Proposta />} />
                <Route path="/compensacoes"        element={<Compensacoes />} />
                <Route path="/relatorios"          element={<Relatorios />} />
                <Route path="/afastamentos"        element={<Afastamentos />} />
                <Route path="/relatorio-vr"        element={<RelatorioVR />} />
                <Route path="/relatorio-vr/:id" element={<VRDetalhe />} />
                <Route path="/vr-apuracao"         element={<VrApuracao />} />
                <Route path="/jornada-extra"       element={<JornadaExtra />} />
                <Route path="/feriados"            element={<Feriados />} />
                <Route path="/ocorrencias-jornada" element={<OcorrenciasJornada />} />
              </Routes>
            </Layout>
          </RequireAuth>
        } />
      </Routes>
    </Router>
  );
}
